// "Próximos pagos": todo lo que va a salir en los próximos días (gastos fijos, cuotas de deudas,
// compromisos y semanas de envíos sin pagar), con su fecha de vencimiento.
import { hoyISO, parseFecha } from './format';
import { gastoFijoPagado, totalAbonadoPorCompromiso } from './finanzas';
import { agruparPorSemana, sumarDias } from './envios';

const num = (n) => Number(n || 0);
const pad = (n) => String(n).padStart(2, '0');

// Fecha del día de vencimiento en un mes dado (si el mes tiene menos días, el último día).
function fechaDelMes(ano, mes, dia) {
  const ultimo = new Date(ano, mes, 0).getDate();
  return `${ano}-${pad(mes)}-${pad(Math.min(dia, ultimo))}`;
}

function sumarMeses(ano, mes, n) {
  const d = new Date(ano, mes - 1 + n, 1);
  return [d.getFullYear(), d.getMonth() + 1];
}

// Próximo vencimiento de un gasto fijo, o null si no tiene día de vencimiento.
// Si el período actual ya está pagado, el vencimiento es el del período siguiente.
function vencimientoGastoFijo(g, hoy) {
  const dia = num(g.dia_vencimiento);
  const fh = parseFecha(hoy);
  const pagado = gastoFijoPagado(g, fh);
  const [ano, mes] = [fh.getFullYear(), fh.getMonth() + 1];
  if (g.frecuencia === 'anual') {
    if (pagado) return { fecha: null, pagado };
    return { fecha: dia ? fechaDelMes(ano, mes, dia) : null, pagado };
  }
  if (g.frecuencia === 'semestral') {
    if (!pagado) return { fecha: dia ? fechaDelMes(ano, mes <= 6 ? 1 : 7, dia) : null, pagado };
    const [a, m] = mes <= 6 ? [ano, 7] : [ano + 1, 1];
    return { fecha: dia ? fechaDelMes(a, m, dia) : null, pagado };
  }
  if (!pagado) return { fecha: dia ? fechaDelMes(ano, mes, dia) : null, pagado };
  const [a, m] = sumarMeses(ano, mes, 1);
  return { fecha: dia ? fechaDelMes(a, m, dia) : null, pagado };
}

export function calcularProximosPagos({
  gastosFijos = [], deudas = [], pagosDeuda = [], compromisos = [], abonos = [],
  envioRetiros = [], envioSemanas = [], hoy = hoyISO(), dias = 30,
}) {
  const limite = sumarDias(hoy, dias);
  const items = [];

  // Gastos fijos pendientes (o el siguiente período, si ya se pagó y vence dentro del plazo)
  gastosFijos.forEach(g => {
    const { fecha, pagado } = vencimientoGastoFijo(g, hoy);
    if (pagado && (!fecha || fecha > limite)) return;
    items.push({
      id: `gf-${g.id}`, tipo: 'gasto_fijo', icono: '🏠', nombre: g.nombre, fecha,
      monto: num(g.monto), apartado: false,
      detalle: g.compartido_con ? `👥 ${g.compartido_pct}% ${g.compartido_con}` : (g.frecuencia !== 'mensual' ? g.frecuencia : ''),
    });
  });

  // Cuotas de deudas activas: la de este mes si falta pagarla; si no, la del mes siguiente.
  deudas.filter(d => num(d.cuota_mensual) > 0).forEach(d => {
    const pagos = pagosDeuda.filter(p => p.deuda_id === d.id);
    const pendiente = Math.max(0, num(d.monto_original) - pagos.reduce((s, p) => s + num(p.monto), 0));
    if (pendiente <= 0) return;
    const fh = parseFecha(hoy);
    const [ano, mes] = [fh.getFullYear(), fh.getMonth() + 1];
    const pagadoEsteMes = pagos.filter(p => p.fecha?.startsWith(`${ano}-${pad(mes)}`)).reduce((s, p) => s + num(p.monto), 0);
    const dia = num(d.dia_vencimiento);
    let fecha = dia ? fechaDelMes(ano, mes, dia) : null;
    if (pagadoEsteMes >= num(d.cuota_mensual)) {
      if (!dia) return;
      const [a, m] = sumarMeses(ano, mes, 1);
      fecha = fechaDelMes(a, m, dia);
      if (fecha > limite) return;
    }
    items.push({
      id: `deuda-${d.id}`, tipo: 'deuda', icono: d.icono || '💳', nombre: `Cuota ${d.nombre}`, fecha,
      monto: Math.min(num(d.cuota_mensual), pendiente), apartado: false,
      detalle: d.acreedor || '',
    });
  });

  // Compromisos pendientes: ya están descontados del disponible real
  const abonado = totalAbonadoPorCompromiso(abonos);
  compromisos.filter(c => c.estado === 'pendiente').forEach(c => {
    const pendiente = Math.max(0, num(c.monto_total) - (abonado[c.id] || 0));
    if (pendiente <= 0) return;
    items.push({
      id: `comp-${c.id}`, tipo: 'compromiso', icono: '📌', nombre: c.nombre, fecha: c.fecha || null,
      monto: pendiente, apartado: true, detalle: 'ya apartado',
    });
  });

  // Semanas de envíos sin pagar: se pagan el lunes siguiente a la semana
  agruparPorSemana(envioRetiros, envioSemanas)
    .filter(s => !s.registro?.pagado && (s.paquetes > 0 || s.registro?.monto_cobrado))
    .forEach(s => {
      items.push({
        id: `envio-${s.inicio}`, tipo: 'envio', icono: '🚚', nombre: `Envíos semana ${s.inicio.slice(8, 10)}/${s.inicio.slice(5, 7)}`,
        fecha: sumarDias(s.fin, 1),
        monto: s.registro?.monto_cobrado != null ? num(s.registro.monto_cobrado) : s.total,
        apartado: false, detalle: `${s.paquetes} paquetes`,
      });
    });

  // Solo lo vencido, lo que vence dentro del plazo y lo que no tiene fecha
  const visibles = items.filter(i => !i.fecha || i.fecha <= limite);
  visibles.sort((a, b) => (a.fecha || '9999-99-99').localeCompare(b.fecha || '9999-99-99') || b.monto - a.monto);

  const total = visibles.reduce((s, i) => s + i.monto, 0);
  const yaApartado = visibles.filter(i => i.apartado).reduce((s, i) => s + i.monto, 0);
  return { items: visibles, total, yaApartado, porApartar: total - yaApartado };
}
